import { Controller } from "@hotwired/stimulus";
import { getDeviceType, getDeviceTypes } from "shared/device_types/device_types";
import TomSelect from "tom-select";
import { updateSelect } from "shared/selects/select_helpers";
import _, { isEmpty } from "lodash";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { allDeviceTypesStore } from "../../../stores/device_types/all_device_types_store";
import { deviceTypeStore } from "../../../stores/device_types/device_type_store";
import { multiDeviceTypeStore } from "../../../stores/device_types/multi_device_type_store";
import { useStore } from "stimulus-store";

// Connects to data-controller="selects--device-types--device-type-select"
export default class extends Controller {

    static stores = [
        allDeviceTypesStore,
        deviceTypeStore,
        multiDeviceTypeStore
    ]

    static values = {
        selected: String
    }

    connect() {
        useStore(this);
    }

    initialize() {
        this._populateSelect();
    }

    clearSelected() {
        // Get the TomSelect instance from the element
        const tomSelectInstance = this.element.tomselect;

        if (tomSelectInstance) {
            // Clear all selected options
            tomSelectInstance.clear();
        }
    }

    _populateSelect() {
        const deviceTypeSelectElement = this.element;
        const deviceTypeId = this.selectedValue;
        const isMultiSelect = this._isMultiSelect();

        getDeviceTypes().then((results) => {
            const control = new TomSelect(deviceTypeSelectElement, {
                placeholder: isMultiSelect ? 'Select device types' : 'Select device type',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'name',
                searchField: 'name',
                sortField: {
                    field: 'name',
                    direction: 'asc'
                },
                onChange: (event) => this._deviceTypeSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (values) => this._deviceTypeUnselectedEvent(values),
                // options: results,
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: !isMultiSelect // Close after select only for single-select
            });

            // Add the expected options to the select. We're doing it this way instead of
            // in the constructor so the items appear in the underlying select. This makes
            // it possible to test using capybara matchers
            updateSelect(this.element.id, results);

            // Add all the fetched device types to the store
            this.setAllDeviceTypesStoreValue(results);

            // Syncing will ensure options are stubbed out within the select element. This
            // ensures the tests will have the data in the DOM it can test against
            control.sync();

            if (!_.isEmpty(deviceTypeId)) {
                // Setting silent to true suppresses change events
                // https://tom-select.js.org/docs/api/
                control.setValue(deviceTypeId, DO_NOT_EMIT_EVENTS);

                // Populate the appropriate store based on multi-select capability
                if (isMultiSelect) {
                    // For multi-select, populate multiDeviceTypeStore
                    const deviceTypeIds = Array.isArray(deviceTypeId) ? deviceTypeId : [deviceTypeId];
                    this._populateMultiDeviceTypeStore(deviceTypeIds);
                } else {
                    // For single-select, populate deviceTypeStore
                    // Await the promise to get the actual device type object
                    getDeviceType(deviceTypeId).then((device_type) => {
                        this.setDeviceTypeStoreValue(device_type);
                    });
                }
            }

            // Issue the selection made event or notify everyone that the page loaded
            // without a selection. We're not using the default event because this fools
            // the manufacturer and model select controllers into thinking nothing is
            // selected.
            this._notifyListenersOfDeviceTypeOnPageLoad(deviceTypeId)
        });
    }

    _deviceTypeSelectionChanged(event) {
        // Simply return we don't have an ID (so no selection was made). This happens
        // during initialization when options are loaded..
        if (isEmpty(event)) {
            return;
        }

        // If the event is a string
        if (typeof event === 'string') {
            // Describe the device type. We need to do this to determine if it's a printer or workstation.
            getDeviceType(event).then((device_type) => {
                // Update the store with the new device type's metadata. Other controllers
                // listen to this store's updates to change the selects or flex other behavior.
                this.setDeviceTypeStoreValue(device_type);

                // TODO: MIGRATE EVERYONE WHO DEPENDS ON THESE EVENTS TO STIMULUS-STORE
                // Send an event telling the name input, parts select, and model select
                // controllers to enable those elements. I stole this method from here:
                // https://lmika.org/2021/02/17/communication-among-stimulus.html
                window.dispatchEvent(new CustomEvent('deviceTypeSelectionMade', {
                    detail: {
                        device_type: device_type.id,
                        printer: device_type.printer,
                        workstation: device_type.workstation
                    },
                    bubbles: true // This needs to be explicitly set to enable event bubbling
                }));
            });
        } else if (event instanceof Array) {
            // Get current store value - ensure it's always an array
            const currentStore = this.multiDeviceTypeStoreValue || [];

            // Safely extract IDs from current store
            const currentDeviceTypeIds = Array.isArray(currentStore) ? currentStore.map(dt => dt && dt.id)
                                                                                   .filter(Boolean) : [];

            // Find newly added device types (in event but not in store)
            const newDeviceTypeIds = event.filter(id => !currentDeviceTypeIds.includes(id));

            // Find removed device types (in store but not in event)
            const removedDeviceTypeIds = currentDeviceTypeIds.filter(id => !event.includes(id));

            // Process newly added device types
            if (newDeviceTypeIds.length > 0) {
                // Fetch device type details for new selections
                const deviceTypePromises = newDeviceTypeIds.map(id => getDeviceType(id));

                Promise.all(deviceTypePromises).then(deviceTypes => {
                    // Add new device types to the store
                    const updatedStore = [...currentStore, ...deviceTypes.filter(dt => dt && dt.id)];
                    this.setMultiDeviceTypeStoreValue(updatedStore);
                }).catch(error => {
                    console.error('Error fetching device type details:', error);
                });
            }

            // Process removed device types
            if (removedDeviceTypeIds.length > 0) {
                // Remove device types from the store
                const updatedStore = currentStore.filter(dt => !removedDeviceTypeIds.includes(dt.id));
                this.setMultiDeviceTypeStoreValue(updatedStore);
            }
        }
    }

    _deviceTypeUnselectedEvent(values) {
        if (this._isMultiSelect()) {
            this.setMultiDeviceTypeStoreValue([]);
        } else {
            // Clear the device type store
            this.setDeviceTypeStoreValue({});

            // TODO: MIGRATE EVERYONE WHO DEPENDS ON THESE EVENTS TO STIMULUS-STORE
            window.dispatchEvent(new Event('deviceTypeSelectionCleared', {
                detail: {},
                bubbles: true // This needs to be explicitly set to enable event bubbling
            }));
        }
    }

    _notifyListenersOfDeviceTypeOnPageLoad(device_type_id) {
        if (isEmpty(device_type_id)) {
            // Send an event telling consumers that the page loaded without a device
            // type selected. I stole this method from here:
            // https://lmika.org/2021/02/17/communication-among-stimulus.html
            window.dispatchEvent(new CustomEvent('pageLoadDeviceType', {
                detail: {
                    device_type: null,
                    printer: false,
                    workstation: false
                },
                bubbles: true // This needs to be explicitly set to enable event bubbling
            }));
        } else {
            // Describe the device type. We need to do this to determine if it's a printer or workstation.
            getDeviceType(device_type_id).then((device_type) => {
                // TODO: DEPRECATE THIS EVENT AND DEPEND ON THE onDeviceStoreUpdate method
                // Send an event telling the name input, parts select, and model select
                // controllers to enable those elements. I stole this method from here:
                // https://lmika.org/2021/02/17/communication-among-stimulus.html
                window.dispatchEvent(new CustomEvent('pageLoadDeviceType', {
                    detail: {
                        device_type: device_type.id,
                        printer: device_type.printer,
                        workstation: device_type.workstation
                    },
                    bubbles: true // This needs to be explicitly set to enable event bubbling
                }));
            });
        }
    }

    /**
     * Check if this select element is configured for multi-select
     */
    _isMultiSelect() {
        return this.element.hasAttribute('multiple') || this.element.multiple === true;
    }

    /**
     * Populate the multiDeviceTypeStore with initial device type IDs
     */
    async _populateMultiDeviceTypeStore(deviceTypeIds) {
        try {
            const deviceTypes = [];

            for (const deviceTypeId of deviceTypeIds) {
                try {
                    const deviceType = await getDeviceType(deviceTypeId);
                    if (deviceType && deviceType.id) {
                        deviceTypes.push(deviceType);
                    }
                } catch (error) {
                    console.error(`Error fetching device type ${deviceTypeId}:`, error);
                }
            }

            // Update the multiDeviceTypeStore
            this.setMultiDeviceTypeStoreValue(deviceTypes);
        } catch (error) {
            console.error('Error populating multi device type store:', error);
        }
    }
}